import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { Box, Link, Paper, Alert, CssBaseline } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import { resetPassword } from "../../../utils/restAPI";
import { updateLoadingAxios } from "../../../Redux/Reducers/uiSlice";
import { useDispatch } from "react-redux";
import { lightAuthTheme, darkAuthTheme } from "../../../Theme/authTheme";
import AuthLogo from "../AuthLogo";
import { useTheme } from "../../../contexts/ThemeContext";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton } from "@mui/material";

const ResetPasswordOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  // Choose the appropriate theme based on current mode
  const authTheme = isDarkMode ? darkAuthTheme : lightAuthTheme;

  // Get email from URL params
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get("email");

  useEffect(() => {
    if (!email) {
      setError("Invalid or missing email. Please request a new password reset.");
    }
  }, [email]);

  const handleResetPassword = async () => {
    if (!verificationCode.trim()) {
      setError("Please enter the verification code");
      return;
    }

    if (!password.trim()) {
      setError("Please enter a new password");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError("Password must contain at least one uppercase letter, one lowercase letter, and one number");
      return;
    }

    try {
      dispatch(updateLoadingAxios(true));
      const response = await resetPassword(email, verificationCode, password);
      dispatch(updateLoadingAxios(false));

      if (response && response.success) {
        setIsSuccess(true);
        setError("");
      } else {
        setError("Invalid verification code or failed to reset password. Please try again.");
      }
    } catch (error) {
      dispatch(updateLoadingAxios(false));
      setError("Something went wrong. Please try again.");
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const handleBackToForgotPassword = () => {
    navigate("/forgot");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (isSuccess) {
    return (
      <ThemeProvider theme={authTheme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: "100vh",
            backgroundColor: authTheme.palette.background.default,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem 1rem",
            zoom: 0.8,
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={0}
              sx={{
                padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
                borderRadius: 3,
                backgroundColor: authTheme.palette.background.paper,
                boxShadow: authTheme.palette.mode === 'dark' 
                  ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                  : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }}
            >
              {/* Logo */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <AuthLogo />
              </Box>

              {/* Success Message */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem" },
                    fontWeight: 700,
                    color: authTheme.palette.text.primary,
                    mb: 2,
                  }}
                >
                  Password Reset Successfully!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: authTheme.palette.text.secondary,
                    fontSize: "1.125rem",
                    mb: 3,
                  }}
                >
                  Your password has been updated. You can now sign in with your new password.
                </Typography>
                <Alert severity="success" sx={{ borderRadius: 2, mb: 3 }}>
                  Password updated successfully!
                </Alert>
              </Box>

              {/* Sign In Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleBackToLogin}
                sx={{
                  mb: 3,
                  height: "56px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor: authTheme.palette.mode === 'dark' ? "#F9FAFB" : "#1F2937",
                  color: authTheme.palette.mode === 'dark' ? "#111827" : "#FFFFFF",
                  "&:hover": {
                    backgroundColor: authTheme.palette.mode === 'dark' ? "#E5E7EB" : "#374151",
                  },
                }}
              >
                Sign In
              </Button>
            </Paper>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={authTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: authTheme.palette.background.default,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem 1rem",
          zoom: 0.8,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
              borderRadius: 3,
              backgroundColor: authTheme.palette.background.paper,
              boxShadow: authTheme.palette.mode === 'dark' 
                ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <AuthLogo />
            </Box>

            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  fontWeight: 700,
                  color: authTheme.palette.text.primary,
                  mb: 2,
                }}
              >
                Reset Your Password
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.text.secondary,
                  fontSize: "1.125rem",
                  mb: 3,
                }}
              >
                We've sent a verification code to:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.primary.main,
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                {email}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.text.secondary,
                  fontSize: "1rem",
                }}
              >
                Enter the verification code and your new password below.
              </Typography>
            </Box>

            {/* Form */}
            <Box sx={{ mt: 3 }}>
              {/* Error Message */}
              {error && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Verification Code Field */}
              <TextField
                fullWidth
                id="verificationCode"
                name="verificationCode"
                label="Verification Code"
                placeholder="Enter verification code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                sx={{ mb: 3 }}
              />

              {/* New Password Field */}
              <TextField
                fullWidth
                id="password"
                name="password"
                label="New Password"
                placeholder="Enter your new password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      sx={{ color: authTheme.palette.text.secondary }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              {/* Confirm Password Field */}
              <TextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm New Password"
                placeholder="Confirm your new password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={toggleConfirmPasswordVisibility}
                      edge="end"
                      sx={{ color: authTheme.palette.text.secondary }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              {/* Submit Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleResetPassword}
                disabled={!verificationCode.trim() || !password.trim() || !confirmPassword.trim()}
                sx={{
                  mb: 3,
                  height: "56px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor: authTheme.palette.mode === 'dark' ? "#F9FAFB" : "#1F2937",
                  color: authTheme.palette.mode === 'dark' ? "#111827" : "#FFFFFF",
                  "&:hover": {
                    backgroundColor: authTheme.palette.mode === 'dark' ? "#E5E7EB" : "#374151",
                  },
                  "&:disabled": {
                    backgroundColor: authTheme.palette.mode === 'dark' ? "#4B5563" : "#D1D5DB",
                    color: authTheme.palette.mode === 'dark' ? "#6B7280" : "#9CA3AF",
                  },
                }}
              >
                Reset Password
              </Button>

              {/* Back to Login Link */}
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: authTheme.palette.text.secondary }}>
                  Remember your password?{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleBackToLogin}
                    sx={{
                      color: authTheme.palette.secondary.main,
                      textDecoration: "none",
                      fontWeight: 600,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Back to Login
                  </Link>
                </Typography>
              </Box>

              {/* Resend Code Section */}
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Typography variant="body2" sx={{ color: authTheme.palette.text.secondary }}>
                  Didn't receive the code?{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleBackToForgotPassword}
                    sx={{
                      color: authTheme.palette.secondary.main,
                      textDecoration: "none",
                      fontWeight: 600,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Request New Code
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ResetPasswordOTP;
